import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import wlLogo from '@images/logos/wl-logo.svg'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import msPrimeLogo from '@images/logos/ms-prime-logo.svg'
import msLogo from '@images/logos/ms-logo.svg'
import msLogoAu from '@images/logos/ms-logo-au-black.svg'
import fpLogo from '@images/logos/fp-logo.svg'
import rexLogo from '@images/logos/rex-logo.svg'
import rexLogoAu from '@images/logos/rex-logo-au-black.svg'
import reLogoVertical from '@images/logos/re-logo-vertical-black.svg'
import reLogoVerticalAu from '@images/logos/re-logo-vertical-au-black.svg'
import tileHighlight from '@images/tile-highlight.svg'
import Component from '@glimmer/component'
import { Product } from 'teacher-ui-client/utils/products'
import { t } from 'ember-intl'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { ButtonAppearance, appearanceToThemeClasses } from 'teacher-ui-client/components/ui-button/themes'

function startButtonUrl(path: string) {
  return join(config.readingEggsBaseUrl, path).href
}

interface Args {
  showABCBranding?: boolean
  product: Product
}

interface Signature {
  Args: Args
  Element: HTMLAnchorElement
}

type ProductClass = {
  [key in Product]: {
    content: string
    buttonAppearance: ButtonAppearance
  }
}

type StartButtonViewModel = {
  name: string
  description?: string
  linkPath: string
  imageSrc: string
}

export class StartButton extends Component<Signature> {
  @service intl!: IntlService

  get highlightImageSrc() {
    return tileHighlight
  }

  get viewModelImageSrc() {
    return this.startButtonViewModel.imageSrc
  }

  get productName() {
    return this.startButtonViewModel.name
  }

  get product() {
    return this.args.product
  }

  get showABCBranding() {
    return this.args.showABCBranding
  }

  get startButtonViewModel(): StartButtonViewModel {
    const viewModels = {
      [Product.RE]: {
        name: this.intl.t('products.reading-eggs'),
        description: this.intl.t('pages.home.links.reading-eggs.description', { htmlSafe: true }),
        linkPath: startButtonUrl('/re/teacher/class_login'),
        imageSrc: this.showABCBranding ? reLogoVerticalAu : reLogoVertical,
      },
      [Product.REX]: {
        name: this.intl.t('products.reading-eggspress'),
        description: this.intl.t('pages.home.links.reading-eggspress.description'),
        linkPath: startButtonUrl('/rex/teacher/class_login'),
        imageSrc: this.showABCBranding ? rexLogoAu : rexLogo,
      },
      [Product.FP]: {
        name: this.intl.t('products.fast-phonics'),
        description: this.intl.t('pages.home.links.fast-phonics.description', { htmlSafe: true }),
        linkPath: startButtonUrl('/fast_phonics/teacher/class_login'),
        imageSrc: fpLogo,
      },
      [Product.MS]: {
        name: this.intl.t('products.mathseeds'),
        description: this.intl.t('pages.home.links.mathseeds.description'),
        linkPath: startButtonUrl('/ms/teacher/class_login'),
        imageSrc: this.showABCBranding ? msLogoAu : msLogo,
      },
      [Product.MSP]: {
        name: this.intl.t('products.mathseeds-prime'),
        description: this.intl.t('pages.home.links.mathseeds-prime.description'),
        linkPath: startButtonUrl('/ms_prime/teacher/class_login'),
        imageSrc: msPrimeLogo,
      },
      [Product.WL]: {
        name: this.intl.t('products.writing-legends'),
        linkPath: startButtonUrl('/wl/teacher/class_login'),
        imageSrc: wlLogo,
      },
    }
    return viewModels[this.product]
  }

  get productClasses() {
    const classes: ProductClass = {
      [Product.RE]: {
        content: 'bg-oceany-blue-50',
        buttonAppearance: ButtonAppearance.specialBlue,
      },
      [Product.REX]: {
        content: `bg-rexy-purple-50`,
        buttonAppearance: ButtonAppearance.specialPurple,
      },
      [Product.FP]: {
        content: 'bg-pink-50',
        buttonAppearance: ButtonAppearance.specialRed,
      },
      [Product.MS]: {
        content: 'bg-green-50',
        buttonAppearance: ButtonAppearance.specialGreen,
      },
      [Product.MSP]: {
        content: 'bg-yellow-25',
        buttonAppearance: ButtonAppearance.specialGreen,
      },
      [Product.WL]: {
        content: 'bg-blue-100',
        buttonAppearance: ButtonAppearance.specialBlue,
      },
    }

    return classes[this.product]
  }

  get buttonAppearance() {
    return appearanceToThemeClasses(this.productClasses.buttonAppearance).join(' ')
  }

  get productAlt() {
    return 'products.' + this.args.product
  }

  <template>
    {{#if this.startButtonViewModel}}
      <div
        class="{{this.productClasses.content}}
          relative flex w-96 flex-col items-center justify-between gap-y-3 rounded-3xl py-4 text-center"
        aria-label={{t "components.startButton.ariaLabel"}}
        data-test-start-button={{@product}}
        ...attributes
      >
        <img
          src={{this.viewModelImageSrc}}
          class="mx-auto h-16"
          alt={{t this.productAlt}}
          data-test-start-button-logo
        />
        <div>
          <p class="text-neutral-250 font-semibold uppercase">{{this.startButtonViewModel.name}}</p>
          <p
            class="{{if this.startButtonViewModel.description 'min-h-12'}} content-center font-medium text-neutral-400"
          >
            {{this.startButtonViewModel.description}}
          </p>
        </div>
        <a
          href={{this.startButtonViewModel.linkPath}}
          class="{{this.buttonAppearance}}
            inline-flex items-center justify-center rounded-md px-5 py-2 font-bold no-underline transition-all duration-200 select-none"
        >
          {{t "components.startButton.label"}}
          <PhosphorIcon @icon="phosphor-arrow-right-bold" class="ml-1" />
        </a>
      </div>
    {{/if}}
  </template>
}

export default StartButton
